import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface ReviewQueueProject {
  id: string;
  name: string;
  customerId: string | null;
  customerName: string;
  opportunityId: string | null;
  opportunityTitle: string | null;
  status: 'submitted' | 'in_review';
  totalCost: number;
  totalAdjustedDays: number;
  currency: 'GBP' | 'USD' | 'EUR';
  reviewerId: string | null;
  reviewerName: string | null;
  reviewerEmail: string | null;
  reviewerAssignedAt: string | null;
  submitterId: string | null;
  submitterName: string | null;
  submittedAt: string;
  createdAt: string;
}

export interface ReviewQueueResponse {
  projects: ReviewQueueProject[];
  summary: {
    total: number;
    unassigned: number;
    submitted: number;
    inReview: number;
  };
}

export function useReviewQueue() {
  return useQuery({
    queryKey: ['reviews', 'queue'],
    queryFn: () => api.get<ReviewQueueResponse>('/reviews/queue'),
    staleTime: 30 * 1000,
  });
}

export function useClaimReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.post(`/reviews/projects/${projectId}/claim`, {}),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useUnassignReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.post(`/reviews/projects/${projectId}/unassign`, {}),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}
